import "./LeadLists.scss"

import { MenuItem, PromptVariant, TableData, TableEmptyMessage } from "@skylead/component-library"
import { debounce } from "lodash"
import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { useLeadListsColumnsHook } from "../../../hooks/leadListsHooks"
import { showPrompt } from "../../../redux/actions"
import { updateFormField, updateFormFields } from "../../../redux/forms/formsActions"
import { deleteLists, exportList, getAllLists } from "../../../redux/list/listActions"
import { getMaxOnPage, setMaxOnPageToLocalStorage } from "../../../utils/app-utils"
import { useCustomMenu, useGlobalTranslation } from "../../../utils/hook"
import RegularLayout from "../../layouts/RegularLayout"
import { LeadListsHeader } from "../../organisms/LeadListsHeader"

export const LeadListsPage = () => {
  const dispatch = useDispatch()
  const { tt } = useGlobalTranslation()

  const { formData, language, lists, total, whitelabelId } = useSelector(state => ({
    formData: state.forms.formData,
    language: state.user.profile.language,
    lists: state.list.lists,
    total: state.list.listsPagination.total,
    whitelabelId: state.user.profile.whitelabelId,
  }))

  const [customMenu, setCustomMenu] = useCustomMenu("custom-menu")

  const [sort, setSort] = useState("ASC")
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [searchValue, setSearchValue] = useState(null)
  const [selectedMaxOnPage, setSelectedMaxOnPage] = useState(getMaxOnPage())

  const fetchLists = useMemo(() => {
    return async ({
      search = searchValue,
      skip = page * selectedMaxOnPage,
      take = selectedMaxOnPage,
      direction = sort,
    } = {}) => {
      setLoading(true)
      await dispatch(
        getAllLists({
          search,
          skip,
          take,
          direction,
        }),
      )
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  useEffect(() => {
    const initializeLists = async () => {
      await fetchLists({ search: "" })
    }

    initializeLists()
  }, [fetchLists])

  const debouncedFetchLists = useMemo(() => {
    return debounce(async search => {
      setPage(0)
      await fetchLists({ search, skip: 0 })
    }, 500)
  }, [fetchLists])

  useEffect(() => {
    if (searchValue !== null) {
      debouncedFetchLists(searchValue)
    }

    return () => {
      debouncedFetchLists.cancel()
    }
  }, [searchValue, debouncedFetchLists])

  const handleDeleteList = listId => {
    dispatch(
      showPrompt(
        PromptVariant.WARNING,
        // TODO PP@Any: Add to translation-ms
        tt("list.table.delete-list.prompt.label"),
        // TODO PP@Any: Add to translation-ms
        tt("list.table.delete-list.prompt.msg"),
        async () => {
          const successfullyDeleted = await dispatch(deleteLists([listId]))

          if (successfullyDeleted) {
            await fetchLists()
          }
        },
        tt("delete"),
      ),
    )
  }

  const handleChangeMaxOnPage = listsPerPage => {
    setSelectedMaxOnPage(listsPerPage)
    setMaxOnPageToLocalStorage(listsPerPage)
    setPage(0)
    fetchLists({ skip: 0, take: listsPerPage })
  }

  const handleChangePage = newPage => {
    setPage(newPage)
    fetchLists({ skip: newPage * selectedMaxOnPage })
  }

  const handleSelectSortClick = () => {
    const newSort = sort === "ASC" ? "DESC" : "ASC"
    setSort(newSort)
    setPage(0)
    fetchLists({ skip: 0, direction: newSort })
  }

  const onExportList = list => {
    dispatch(exportList(tt, list))
  }

  const renderLeadListsMenu = (event, list = {}) => {
    setCustomMenu(
      event,
      <>
        <MenuItem
          icon="file-export-20"
          label={tt("export")}
          onClickContainer={() => onExportList(list)}
        />
        <MenuItem
          icon="trash-20"
          // TODO PP@Any: Add delete-list key to translation-ms
          label={tt("delete-list")}
          danger
          onClickContainer={() => handleDeleteList(list.id)}
        />
      </>,
    )
  }

  const leadListsColumns = useLeadListsColumnsHook(renderLeadListsMenu, language)
  const tableProps = {
    tableAriaLabel: "Lead lists table",
    identifier: "lead-lists-order",
    column: "listName",
    columns: leadListsColumns,
    selectSort: handleSelectSortClick,
    sort,
    data: lists,
    loading,
    count: total,
    page,
    changePage: handleChangePage,
    maxOnPage: selectedMaxOnPage,
    handleChangeMaxOnPage,
    emptyState: (
      <TableEmptyMessage
        lastQueriedSearch={searchValue}
        icon="illustration-style-06"
        svgClassName="illustration-svg-empty-table"
        illustrationMessage={tt("no-lists.table-data.msg")}
        tt={tt}
      />
    ),
  }

  return (
    <RegularLayout title={tt("lead-lists")}>
      {customMenu}
      <LeadListsHeader searchValue={searchValue} onSearchValueChange={setSearchValue} />
      <TableData
        {...tableProps}
        tt={tt}
        dispatch={dispatch}
        updateFormField={updateFormField}
        updateFormFields={updateFormFields}
        formData={formData}
        whitelabelId={whitelabelId}
        bodyClassName="lead-lists-table"
      />
    </RegularLayout>
  )
}
